import { useFadeIn } from '../../../hooks/useFadeIn';

export default function WhiteningIntro() {
  const heading = useFadeIn({ direction: 'up' });
  const body = useFadeIn({ direction: 'up', delay: 200 });
  const img = useFadeIn({ direction: 'right', delay: 100 });

  return (
    <>
      {/* Section 1: 不安なまま */}
      <section className="py-32 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-20 items-center">
            <div className="lg:col-span-2">
              <div
                ref={img.ref}
                style={img.style}
                className="rounded-3xl overflow-hidden border border-gray-200 aspect-[4/5]"
              >
                <img
                  src="https://public.readdy.ai/ai/img_res/69e209e47d7d411c60d27652ee629021.jpg"
                  alt="診療室の静謐な空間"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="lg:col-span-3">
              <div ref={heading.ref} style={heading.style}>
                <h2 className="text-3xl lg:text-4xl text-gray-900 mb-16 leading-relaxed font-serif">
                  不安なまま、治療を始めることはありません。
                </h2>
              </div>
              <div ref={body.ref} style={body.style}>
                <div className="space-y-10 text-gray-700">
                  <p>
                    ホワイトニングに興味はあるけれど、<br />
                    「本当に大丈夫かな」<br />
                    「しみたりしないかな」<br />
                    と不安を感じている方も多いと思います。
                  </p>
                  <p>当院では、いきなり施術を行うことはありません。</p>
                  <div className="space-y-4 pl-6 border-l-2 border-gray-300">
                    <p>・本当に白くなるのか</p>
                    <p>・しみる可能性はあるのか</p>
                    <p>・不自然な白さにならないか</p>
                  </div>
                  <p>
                    こうした点を、<br />
                    納得いただけるまで無料カウンセリングで丁寧にご説明したうえで、<br />
                    施術を行っています。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: この選択でよかった */}
      <WhiteningChoice />

      {/* Section 3: 医療としてホワイトニングを行う理由 */}
      <WhiteningMedical />

      {/* Section 4: 症例紹介 */}
      <CaseStudies />
    </>
  );
}

function WhiteningChoice() {
  const heading = useFadeIn({ direction: 'up' });
  const img = useFadeIn({ direction: 'up', delay: 100 });
  const body = useFadeIn({ direction: 'up', delay: 200 });

  return (
    <section className="py-32 px-6 lg:px-12 bg-stone-50">
      <div className="max-w-3xl mx-auto text-center">
        <div ref={heading.ref} style={heading.style}>
          <h2 className="text-3xl lg:text-4xl text-gray-900 mb-12 leading-relaxed font-serif">
            「この選択でよかった」<br />
            そう思っていただくために。
          </h2>
        </div>
        <div ref={img.ref} style={img.style} className="w-full max-w-2xl mx-auto mb-20 rounded-2xl overflow-hidden">
          <img
            src="https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/bbbe273c753ee5f18aa147ed45dfa773.png"
            alt="笑顔の女性"
            className="w-full h-auto object-cover object-top"
          />
        </div>
        <div ref={body.ref} style={body.style}>
          <div className="text-left space-y-12 text-gray-700">
            <p>
              ホワイトニングを終えたあと、<br />
              多くの方が口にされるのは、<br />
              「自分に合った選択ができたと思えた」<br />
              という言葉です。
            </p>
            <p>
              私たちが大切にしているのは、<br />
              白さそのものだけではありません。
            </p>
            <p>
              ご自身で選んだという納得感と、<br />
              その判断に自信を持てること。
            </p>
            <p className="text-sm text-gray-600">
              それが、<br />
              自然な笑顔や前向きな気持ちにつながると<br />
              考えています。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhiteningMedical() {
  const tag = useFadeIn({ direction: 'up' });
  const heading = useFadeIn({ direction: 'up', delay: 100 });
  const body = useFadeIn({ direction: 'up', delay: 200 });
  const img = useFadeIn({ direction: 'up', delay: 150 });
  const essenceHeading = useFadeIn({ direction: 'up' });
  const essenceBody = useFadeIn({ direction: 'up', delay: 150 });
  const closing = useFadeIn({ direction: 'up' });

  return (
    <section className="py-32 px-6 lg:px-12 bg-white">
      <div className="max-w-4xl mx-auto space-y-40">
        {/* 医療としてホワイトニングを行う理由 */}
        <div>
          <div ref={tag.ref} style={tag.style}>
            <p className="text-xs text-gray-500 mb-6 tracking-widest">MEDICAL APPROACH</p>
          </div>
          <div ref={heading.ref} style={heading.style}>
            <h2 className="text-3xl lg:text-4xl text-gray-900 mb-12 leading-relaxed font-serif">
              医療としてホワイトニングを行う理由
            </h2>
          </div>
          <div ref={body.ref} style={body.style}>
            <div className="space-y-10 text-gray-700">
              <p>
                ホワイトニングは、<br />
                ただ歯を白くする処置ではありません。
              </p>
              <p>
                どこまで白くするのか。<br />
                どこで止めるのか。
              </p>
              <p>
                その判断には、<br />
                歯科医療としての知識と責任が伴います。
              </p>
              <p>
                当院では、<br />
                歯科医師・歯科衛生士が管理のもと、<br />
                ホワイトニングを医療として行っています。
              </p>
              <div className="pl-4 border-l-4 border-gray-900">
                <p>
                  私たちが大切にしているのは、<br />
                  「どれくらい白くできるか」だけではなく、<br />
                  健康面を含めて、どこまでが適切な白さなのかを<br />
                  判断することです。
                </p>
              </div>
              <div ref={img.ref} style={img.style} className="w-full max-w-2xl rounded-2xl overflow-hidden mt-10">
                <img
                  src="https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/dda6de9d03e825817872d3673da1f9b2.jpeg"
                  alt="医療ホワイトニングの様子"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ホワイトエッセンス */}
        <div>
          <div ref={essenceHeading.ref} style={essenceHeading.style}>
            <h2 className="text-3xl lg:text-4xl text-gray-900 mb-12 leading-relaxed font-serif">
              医療ホワイトニング「ホワイトエッセンス」について
            </h2>
          </div>
          <div ref={essenceBody.ref} style={essenceBody.style}>
            <div className="w-full max-w-md mb-12">
              <img
                src="https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/88a0223ddcf0c8b765bb1a5ff9c05b63.jpeg"
                alt="ホワイトエッセンス"
                className="w-full h-auto object-contain"
              />
            </div>
            <div className="space-y-10 text-gray-700">
              <p>
                ホワイトエッセンスは、歯のホワイトニングがほとんどの方に認知されていなかった2001年からサービスを開始し、医療サービスを提供してきました。
              </p>
              <div className="mt-8">
                <a
                  href="https://www.whiteessence.com/"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center gap-2 border border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white text-sm px-8 py-3.5 rounded-full transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  ホワイトエッセンスについて
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-sm"></i>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* クロージング */}
        <div ref={closing.ref} style={closing.style} className="text-center pt-20">
          <p className="text-xl text-gray-900 leading-relaxed">
            白さを、感覚ではなく<br />
            医療として設計する。
          </p>
          <p className="text-base text-gray-700 mt-8">
            私たちは、<br />
            この姿勢を何より大切にしています。
          </p>
        </div>
      </div>
    </section>
  );
}

function CaseStudies() {
  const tag = useFadeIn({ direction: 'up' });
  const heading = useFadeIn({ direction: 'up', delay: 100 });
  const case1 = useFadeIn({ direction: 'up', delay: 150 });
  const case2 = useFadeIn({ direction: 'up', delay: 250 });
  const note = useFadeIn({ direction: 'up', delay: 300 });

  return (
    <section className="py-32 px-6 lg:px-12 bg-stone-50">
      <div className="max-w-5xl mx-auto">
        <div ref={tag.ref} style={tag.style}>
          <p className="text-xs text-gray-500 mb-6 tracking-widest">CASE STUDIES</p>
        </div>
        <div ref={heading.ref} style={heading.style}>
          <h2 className="text-3xl lg:text-4xl text-gray-900 mb-20 leading-relaxed font-serif">
            症例紹介
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* 症例1 */}
          <div ref={case1.ref} style={case1.style} className="bg-white rounded-2xl overflow-hidden border border-gray-100">
            <div className="grid grid-cols-2">
              <div className="relative">
                <div className="absolute top-3 left-3 z-10 text-white text-xs px-2.5 py-1 rounded-full tracking-widest" style={{ backgroundColor: '#3E5F8F' }}>
                  BEFORE
                </div>
                <div className="aspect-square overflow-hidden">
                  <img
                    src="https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/7a1108ed2c1043af48c57bdc3d4c00ce.png"
                    alt="症例1 ビフォー"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="relative">
                <div className="absolute top-3 left-3 z-10 bg-white text-xs px-2.5 py-1 rounded-full tracking-widest" style={{ color: '#3E5F8F' }}>
                  AFTER
                </div>
                <div className="aspect-square overflow-hidden">
                  <img
                    src="https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/035de99da7053b4c6bdce7bd17f839fc.png"
                    alt="症例1 アフター"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
            <div className="p-8 space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs text-gray-500 tracking-widest">CASE 01</span>
                <span className="w-8 h-px bg-gray-300"></span>
                <span className="text-xs text-gray-500">30代女性 / ホワイトエッセンスコース</span>
              </div>
              <h3 className="text-base font-medium text-gray-900 leading-relaxed font-serif">
                全体的な黄ばみが気になっていた方
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                もともと歯の色が黄みがかっており、笑顔に自信が持てないとのことでご来院。カウンセリングにて歯の状態を確認し、ホワイトエッセンスコースでホームホワイトニング1か月半とクリーニング、オフィスホワイトニング3回を実施して約1.5か月で変化を実感。施術後はご本人が希望されていた自然な白さに仕上がりました。
              </p>
              <div className="pt-4 border-t border-gray-100">
                <div className="text-xs text-gray-400 leading-relaxed space-y-1">
                  <p>治療名： ホワイトエッセンスコース（自由診療）</p>
                  <p>治療内容： 歯科医院でのオフィスホワイトニング3回と、自宅でのホームホワイトニングを組み合わせたプログラム</p>
                  <p>費用： 118,800円（税込）</p>
                  <p>治療期間・回数： 1.5か月、通院5回</p>
                  <p>副作用・リスク： 個人差はありますが、施術中や施術後に一時的に歯がしみる（知覚過敏）症状が出る場合があります。</p>
                </div>
              </div>
            </div>
          </div>

          {/* 症例2 */}
          <div ref={case2.ref} style={case2.style} className="bg-white rounded-2xl overflow-hidden border border-gray-100">
            <div className="grid grid-cols-2">
              <div className="relative">
                <div className="absolute top-3 left-3 z-10 text-white text-xs px-2.5 py-1 rounded-full tracking-widest" style={{ backgroundColor: '#3E5F8F' }}>
                  BEFORE
                </div>
                <div className="aspect-square overflow-hidden">
                  <img
                    src="https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/5fb402e285fa17cc83e026c901813aa0.png"
                    alt="症例2 ビフォー"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="relative">
                <div className="absolute top-3 left-3 z-10 bg-white text-xs px-2.5 py-1 rounded-full tracking-widest" style={{ color: '#3E5F8F' }}>
                  AFTER
                </div>
                <div className="aspect-square overflow-hidden">
                  <img
                    src="https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/6c08f6f8da560fa870e9f8b7098edb11.png"
                    alt="症例2 アフター"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
            <div className="p-8 space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs text-gray-500 tracking-widest">CASE 02</span>
                <span className="w-8 h-px bg-gray-300"></span>
                <span className="text-xs text-gray-500">40代男性 / ホワイトエッセンスコース</span>
              </div>
              <h3 className="text-base font-medium text-gray-900 leading-relaxed font-serif">
                前歯の黄ばみが気になっていた方
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                前歯の黄ばみが気になり、オンラインで映る自分の表情が気になるとのことでご来院。カウンセリングにて歯の状態を確認し、ホワイトエッセンスコースでホームホワイトニング2月とクリーニング、オフィスホワイトニング3回を実施して約2か月で変化を実感。施術後はご本人が希望されていた自然な白さに仕上がりました。
              </p>
              <div className="pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-400 leading-relaxed">
                  治療名： ホワイトエッセンスコース（自由診療）治療内容： 歯科医院でのオフィスホワイトニング3回と、自宅でのホームホワイトニングを組み合わせたプログラム費用： 118,800円（税込）治療期間・回数： 2か月、通院6回副作用・リスク： 個人差はありますが、施術中や施術後に一時的に歯がしみる（知覚過敏）症状が出る場合があります。
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 適応除外・注意事項 */}
        <div ref={note.ref} style={note.style} className="mt-16 bg-white border border-gray-200 rounded-2xl p-8">
          <p className="text-xs text-gray-500 tracking-widest mb-5">IMPORTANT NOTICE</p>
          <h4 className="text-sm font-medium text-gray-800 mb-4">ホワイトニングが適応とならない場合について</h4>
          <ul className="space-y-2.5 text-sm text-gray-600 leading-relaxed">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 shrink-0"></span>
              <span>妊娠中・授乳中の方</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 shrink-0"></span>
              <span>重度の知覚過敏がある方</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 shrink-0"></span>
              <span>歯周病・むし歯の治療が完了していない方</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 shrink-0"></span>
              <span>差し歯・セラミック・詰め物など人工歯（ホワイトニング剤は天然歯にのみ作用します）</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 shrink-0"></span>
              <span>テトラサイクリン系抗生物質による変色歯（効果が限定的な場合があります）</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 shrink-0"></span>
              <span>16歳未満の方</span>
            </li>
          </ul>
          <p className="text-xs text-gray-400 mt-6 leading-relaxed">
            上記に該当する場合でも、まずはカウンセリングにてご相談ください。歯科医師が状態を確認し、適切なご案内をいたします。
          </p>
        </div>
      </div>
    </section>
  );
}
